// packages/frontend/src/app/sections/Header.tsx
import React, { useCallback } from 'react';
import { useSession } from '../providers/SessionProvider';
import AgeVerifButton from '../../features/payment/AgeVerifButton';
import { navigateInApp } from '../../utils/telegram';

const PAYMENT_PATH: string =
  (import.meta as any)?.env?.VITE_PAYMENT_PATH || '/payment';

function Header() {
  const { isAuthenticated, me, refresh, logout } = useSession();

  const goHome = useCallback(() => navigateInApp('/'), []);
  const goSettings = useCallback(() => navigateInApp('/settings'), []);

  const onLogin = useCallback(() => {
    // Keep navigation same-origin to play nice with Telegram in-app
    navigateInApp(PAYMENT_PATH);
  }, []);

  const onLogout = useCallback(async () => {
    try {
      if (typeof logout === 'function') {
        await logout();
      } else {
        // Fallback if provider doesn't expose logout()
        await fetch('/logout', { method: 'POST', credentials: 'include' });
      }
    } catch {
      // no-op
    } finally {
      await Promise.resolve(refresh?.());
      navigateInApp('/');
    }
  }, [logout, refresh]);

  return (
    <header
      className="ar-header"
      role="banner"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-muted, rgba(0,0,0,0.08))',
        backdropFilter: 'saturate(180%) blur(8px)',
      }}
    >
      {/* Brand */}
      <button
        type="button"
        onClick={goHome}
        className="ar-brand"
        aria-label="Go to home"
        title="Aino Reira App"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: '1rem',
          fontWeight: 600,
        }}
      >
        <span aria-hidden>✨</span>
        <span>Aino Reira App</span>
      </button>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Right cluster (state-sensitive) */}
      {!isAuthenticated ? (
        <div className="ar-actions" style={{ display: 'flex', gap: '8px' }}>
          {/* Age verification entry */}
          <AgeVerifButton />

          {/* Login */}
          <button
            type="button"
            onClick={onLogin}
            className="btn btn-primary"
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-muted, rgba(0,0,0,0.08))',
              background: 'var(--btn-bg, #111)',
              color: 'var(--btn-fg, #fff)',
              fontWeight: 600,
            }}
          >
            Login
          </button>
        </div>
      ) : (
        <div className="ar-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Optional: tiny identity hint */}
          {me?.email ? (
            <span
              title={String(me.email)}
              style={{
                fontSize: '0.9rem',
                opacity: 0.8,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 240,
              }}
            >
              {me.email}
            </span>
          ) : null}

          {/* Settings */}
          <button
            type="button"
            onClick={goSettings}
            className="btn"
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-muted, rgba(0,0,0,0.12))',
              background: 'transparent',
              fontWeight: 600,
            }}
          >
            Settings
          </button>

          {/* Logout */}
          <button
            type="button"
            onClick={onLogout}
            className="btn btn-danger"
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-muted, rgba(0,0,0,0.12))',
              background: 'var(--btn-danger-bg, #c62828)',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            Logout
          </button>
        </div>
      )}
    </header>
  );
}

export default React.memo(Header);
